import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { ChevronDownIcon } from "primereact/icons/chevrondown";
import { ChevronUpIcon } from "primereact/icons/chevronup";
import { MultiSelect } from "primereact/multiselect";
import { OverlayPanel } from "primereact/overlaypanel";

import type {
  FilterOption,
  FilterPanelColumn,
  FilterPanelValue,
  FilterSelectionMode,
} from "./filter-panel.types";
import styles from "./filter-panel.module.scss";

export const FILTERS_LABEL = "Filtros";
export const SAVED_FILTERS_PLACEHOLDER = "Filtros Salvos";
export const SAVED_FILTERS_UNAVAILABLE_TITLE = "Disponível em uma próxima entrega.";
export const COLUMN_PLACEHOLDER = "Selecionar filtro";
export const VALUES_PLACEHOLDER = "Selecionar";
export const ADD_FILTER_LABEL = "Adicionar filtro";
export const APPLY_FILTERS_LABEL = "Aplicar Filtros";
export const SAVE_FILTER_LABEL = "Salvar Filtro";
export const SAVE_FILTER_UNAVAILABLE_TITLE = "Disponível em uma próxima entrega.";
export const REMOVE_FILTER_LABEL = "Remover filtro";
export const FILTER_LOADING_MESSAGE = "Carregando opções...";
export const FILTER_ERROR_MESSAGE = "Não foi possível carregar as opções.";
export const FILTER_EMPTY_MESSAGE = "Nenhuma opção disponível.";
export const FILTER_NO_RESULTS_MESSAGE = "Nenhuma opção encontrada.";

export interface FilterPanelProps {
  /** Colunas filtráveis oferecidas na lista "Selecionar filtro". */
  columns: FilterPanelColumn[];
  /** Critérios em vigor, por coluna. */
  value: FilterPanelValue;
  onChange: (value: FilterPanelValue) => void;
  testId: string;
}

/** Uma linha do construtor: a coluna escolhida e os valores dela. */
interface FilterRow {
  id: number;
  column: string | null;
}

/** Identidade de um conjunto de critérios, para detectar mudança vinda de fora. */
function serialize(value: FilterPanelValue): string {
  return JSON.stringify(
    Object.keys(value)
      .sort()
      .map((key) => [key, value[key] ?? []]),
  );
}

/** Uma linha por coluna com valor aplicado; sem nenhum critério, uma linha em branco. */
function buildRows(columns: FilterPanelColumn[], value: FilterPanelValue): FilterRow[] {
  const applied = columns.filter(({ key }) => (value[key] ?? []).length > 0);

  if (applied.length === 0) return [{ id: 0, column: null }];

  return applied.map((column, index) => ({ id: index, column: column.key }));
}

/**
 * Novo padrão de filtro do Hub (US-1790 / AC6), conforme o Figma
 * (node `7353-123762`): a ação **Filtros** abre um painel em que o usuário monta
 * os critérios linha a linha — escolhe a coluna, escolhe os valores dela e pode
 * acrescentar outra linha em "Adicionar filtro".
 *
 * Componente próprio, criado do zero e sem relação com os `Dropdown` de coluna
 * das telas do Painel de Controle, que seguem inalteradas por decisão do
 * stakeholder. É parametrizável pelas colunas, pela origem das opções, pelo modo
 * de seleção e pelo estado de carregamento/erro de cada coluna, de modo que uma
 * única implementação atenda a todas as colunas filtráveis da tela.
 *
 * A escolha só entra em vigor em "Aplicar Filtros": o painel guarda um rascunho
 * local dos critérios e só os repassa à tela quando o usuário confirma, sem
 * refazer a consulta a cada valor marcado ou desmarcado.
 */
export function FilterPanel({ columns, value, onChange, testId }: FilterPanelProps) {
  const panel = useRef<OverlayPanel>(null);
  const [isOpen, setIsOpen] = useState(false);
  // Qual select de linha está expandido no momento, para o chevron virar com ele.
  const [openField, setOpenField] = useState<string | null>(null);
  // Rascunho local: os critérios em construção, ainda não repassados à tela.
  const [draft, setDraft] = useState<FilterPanelValue>(value);
  const [rows, setRows] = useState<FilterRow[]>(() => buildRows(columns, value));
  const nextId = useRef(rows.length);

  const valueKey = serialize(value);
  // Guarda o último conjunto emitido daqui: o que não bate com ele veio de fora
  // (a ação "Limpar Filtros" do empty state, por exemplo) e reconstrói o rascunho.
  const emittedKey = useRef(valueKey);

  useEffect(() => {
    if (emittedKey.current === valueKey) return;

    emittedKey.current = valueKey;
    setDraft(value);
    setRows(buildRows(columns, value));
    // `columns` só muda quando as opções carregam, o que não redesenha as linhas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey]);

  const columnByKey = useMemo(
    () => new Map(columns.map((column) => [column.key, column])),
    [columns],
  );

  /** Colunas escolhidas nas outras linhas não reaparecem na lista desta. */
  function availableColumns(row: FilterRow) {
    const taken = new Set(
      rows.filter((other) => other.id !== row.id && other.column).map((other) => other.column),
    );

    return columns.filter((column) => !taken.has(column.key));
  }

  const activeCount = columns.filter(({ key }) => (value[key] ?? []).length > 0).length;
  const canAddRow = rows.length < columns.length;

  function emit(next: FilterPanelValue) {
    emittedKey.current = serialize(next);
    onChange(next);
  }

  function handleColumn(row: FilterRow, column: string | null) {
    setRows((current) =>
      current.map((other) => (other.id === row.id ? { ...other, column } : other)),
    );

    // Trocar a coluna da linha abandona os valores da coluna anterior.
    if (row.column && (draft[row.column] ?? []).length > 0) {
      const previousColumn = row.column;
      setDraft((current) => ({ ...current, [previousColumn]: [] }));
    }
  }

  function handleValues(column: string, values: string[]) {
    setDraft((current) => ({ ...current, [column]: values }));
  }

  function handleAddRow() {
    setRows((current) => [...current, { id: nextId.current++, column: null }]);
  }

  /** Remover a linha descarta o critério dela no rascunho; a última linha volta em branco. */
  function handleRemoveRow(row: FilterRow) {
    setRows((current) => {
      const remaining = current.filter((other) => other.id !== row.id);
      return remaining.length > 0 ? remaining : [{ id: nextId.current++, column: null }];
    });

    if (row.column && (draft[row.column] ?? []).length > 0) {
      const removedColumn = row.column;
      setDraft((current) => ({ ...current, [removedColumn]: [] }));
    }
  }

  /** Repassa o rascunho à tela e fecha o painel: só aqui a consulta é refeita. */
  function handleApply() {
    emit(draft);
    panel.current?.hide();
  }

  /**
   * O `Dropdown` já troca sozinho entre os ícones de abrir/fechar; só o
   * `MultiSelect` precisa desse controle manual (sem esse recurso nativo).
   */
  function openHandlers(id: string) {
    return {
      onShow: () => setOpenField(id),
      onHide: () => setOpenField((current) => (current === id ? null : current)),
    };
  }

  function renderValues(row: FilterRow, index: number) {
    const column = row.column ? columnByKey.get(row.column) : undefined;
    const options: FilterOption[] = column?.options ?? [];
    const selected = column ? (draft[column.key] ?? []) : [];
    const mode: FilterSelectionMode = column?.mode ?? "multiple";
    const rowTestId = `${testId}-values-${index}`;

    const emptyMessage = column?.hasError ? FILTER_ERROR_MESSAGE : FILTER_EMPTY_MESSAGE;
    const placeholder = column?.isLoading ? FILTER_LOADING_MESSAGE : VALUES_PLACEHOLDER;
    const ariaLabel = column ? `Valores de ${column.label}` : VALUES_PLACEHOLDER;

    if (mode === "single") {
      return (
        <Dropdown
          className={styles.select}
          panelClassName={styles.selectPanel}
          value={selected[0] ?? null}
          options={options}
          optionLabel="label"
          optionValue="value"
          placeholder={placeholder}
          filter
          filterPlaceholder={column?.searchPlaceholder ?? "Buscar"}
          emptyMessage={emptyMessage}
          emptyFilterMessage={FILTER_NO_RESULTS_MESSAGE}
          disabled={!column || column.isLoading}
          showClear
          onChange={(event) =>
            column
              ? handleValues(column.key, event.value ? [event.value as string] : [])
              : undefined
          }
          aria-label={ariaLabel}
          data-testid={rowTestId}
        />
      );
    }

    return (
      <MultiSelect
        className={styles.select}
        panelClassName={styles.selectPanel}
        value={selected}
        options={options}
        optionLabel="label"
        optionValue="value"
        placeholder={placeholder}
        filter
        filterPlaceholder={column?.searchPlaceholder ?? "Buscar"}
        emptyMessage={emptyMessage}
        emptyFilterMessage={FILTER_NO_RESULTS_MESSAGE}
        disabled={!column || column.isLoading}
        showSelectAll={false}
        maxSelectedLabels={1}
        selectedItemsLabel="{0} selecionados"
        dropdownIcon={openField === rowTestId ? <ChevronUpIcon /> : <ChevronDownIcon />}
        {...openHandlers(rowTestId)}
        onChange={(event) =>
          column ? handleValues(column.key, (event.value as string[]) ?? []) : undefined
        }
        aria-label={ariaLabel}
        data-testid={rowTestId}
      />
    );
  }

  return (
    <div className={styles.filterPanel} data-testid={`${testId}-field`}>
      <Button
        type="button"
        className={styles.trigger}
        icon="pi pi-filter"
        label={FILTERS_LABEL}
        // Com o painel fechado, o contador é a única pista de que há critérios aplicados.
        badge={activeCount > 0 ? String(activeCount) : undefined}
        badgeClassName={styles.badge}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={activeCount > 0 ? `${FILTERS_LABEL} (${activeCount} aplicados)` : FILTERS_LABEL}
        onClick={(event) => panel.current?.toggle(event)}
        data-testid={testId}
      />

      <OverlayPanel
        ref={panel}
        className={styles.panel}
        onShow={() => setIsOpen(true)}
        onHide={() => setIsOpen(false)}
        dismissable
      >
        <div className={styles.panelBody} data-testid={`${testId}-panel`}>
          <header className={styles.header}>
            <h2 className={styles.headerTitle}>{FILTERS_LABEL}</h2>

            {/* Filtros salvos são história própria: o campo existe como ponto de
                entrada previsto no padrão, ainda sem nenhuma origem de dados. */}
            <Dropdown
              className={styles.savedFilters}
              value={null}
              options={[]}
              placeholder={SAVED_FILTERS_PLACEHOLDER}
              title={SAVED_FILTERS_UNAVAILABLE_TITLE}
              disabled
              aria-label={SAVED_FILTERS_PLACEHOLDER}
              data-testid={`${testId}-saved`}
            />
          </header>

          <div className={styles.group}>
            {rows.map((row, index) => (
              <div key={row.id} className={styles.row} data-testid={`${testId}-row-${index}`}>
                <div className={styles.rowFields}>
                  <Dropdown
                    className={`${styles.select} ${styles.columnSelect}`}
                    panelClassName={styles.selectPanel}
                    value={row.column}
                    options={availableColumns(row)}
                    optionLabel="label"
                    optionValue="key"
                    placeholder={COLUMN_PLACEHOLDER}
                    onChange={(event) => handleColumn(row, (event.value as string | null) ?? null)}
                    aria-label={COLUMN_PLACEHOLDER}
                    data-testid={`${testId}-column-${index}`}
                  />

                  {row.column ? renderValues(row, index) : null}
                </div>

                <div className={styles.rowActions}>
                  <Button
                    type="button"
                    className={styles.saveButton}
                    label={SAVE_FILTER_LABEL}
                    title={SAVE_FILTER_UNAVAILABLE_TITLE}
                    disabled
                    data-testid={`${testId}-save-${index}`}
                    text
                  />

                  <Button
                    type="button"
                    className={styles.removeButton}
                    icon="pi pi-trash"
                    aria-label={
                      row.column
                        ? `${REMOVE_FILTER_LABEL} de ${columnByKey.get(row.column)?.label ?? ""}`.trim()
                        : REMOVE_FILTER_LABEL
                    }
                    onClick={() => handleRemoveRow(row)}
                    data-testid={`${testId}-remove-${index}`}
                    text
                  />
                </div>
              </div>
            ))}
          </div>

          <footer className={styles.footer}>
            <Button
              type="button"
              className={styles.addButton}
              icon="pi pi-plus"
              label={ADD_FILTER_LABEL}
              disabled={!canAddRow}
              onClick={handleAddRow}
              data-testid={`${testId}-add`}
              text
            />

            <Button
              type="button"
              className={styles.applyButton}
              label={APPLY_FILTERS_LABEL}
              onClick={handleApply}
              data-testid={`${testId}-apply`}
            />
          </footer>
        </div>
      </OverlayPanel>
    </div>
  );
}
